import { Add, Delete, Edit, List, Sort as SortIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { navigate, useLocation } from '@reach/router';
import { useEffect, useState } from 'react';
import apiClient from '../api/client';
import AsyncTableSearch from '../components/AsyncTableSearch';
import Layout from '../components/Layout';
import SchemaEditor from '../components/SchemaEditor';
import SectionItems from '../components/SectionItems';
import SectionScripts from '../components/SectionScripts';
import StyledTableContainer from '../components/StyledTableContainer';
import { useAuth } from '../contexts/AuthContext';

const SectionsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const querySectionId = searchParams.get('sectionId');
  const [sections, setSections] = useState([]);
  const [filteredSections, setFilteredSections] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    schema: []
  });
  const [selectedSection, setSelectedSection] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [totalPages, setTotalPages] = useState(null);

  // Состояние для пагинации поиска
  const [searchActive, setSearchActive] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchPagination, setSearchPagination] = useState({ page: 1, pages: 1, total: 0, perPage: 20 });

  // Сортировка
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (user) {
      fetchSections();
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (sections.length > 0 && querySectionId) {
      const sec = sections.find(s => s.id === querySectionId);
      if (sec) {
        setSelectedSection(sec);
        setActiveTab(1);
      }
    }
  }, [sections, querySectionId]);

  // Фильтрация разделов локально (только при неактивном серверном поиске)
  useEffect(() => {
    // Если активен серверный поиск - не делаем локальную фильтрацию
    if (searchActive) return;

    if (sections.length > 0) {
      let filtered = sections.filter(section =>
        section.name.toLowerCase().includes(searchQuery.toLowerCase())
      );

      // Применяем сортировку
      filtered = filtered.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        // Обработка вложенных полей (customFields)
        if (sortBy.includes('customFields.')) {
          const field = sortBy.replace('customFields.', '');
          aValue = a.customFields?.[field];
          bValue = b.customFields?.[field];
        }

        // Обработка пустых значений
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Сравнение
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue?.toLowerCase() || '';
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });

      setFilteredSections(filtered);
    } else {
      setFilteredSections([]);
    }
  }, [searchQuery, sections, searchActive, sortBy, sortOrder]);

  const fetchSections = async (pageParam = page) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/sections?page=${pageParam}&per_page=${perPage}`);
      setSections(response.data);
      const pagesMeta = response.pagination?.pages ?? parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages']);
      if (!isNaN(pagesMeta)) setTotalPages(pagesMeta);
      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке разделов:', err);
      setError('Не удалось загрузить список разделов');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (section = null) => {
    if (section) {
      setEditingSection(section);
      setFormData({
        name: section.name,
        schema: section.schema || []
      });
    } else {
      setEditingSection(null);
      setFormData({
        name: '',
        schema: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSection) {
        await apiClient.put(`/sections/${editingSection.id}`, formData);
      } else {
        await apiClient.post('/sections', formData);
      }
      handleCloseDialog();
      fetchSections();
    } catch (err) {
      console.error('Ошибка при сохранении раздела:', err);
      setError('Не удалось сохранить раздел');
    }
  };

  const handleDelete = async (sectionId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот раздел?')) {
      try {
        await apiClient.delete(`/sections/${sectionId}`);
        fetchSections();
        if (selectedSection?.id === sectionId) {
          setSelectedSection(null);
          setActiveTab(0);
        }
      } catch (err) {
        console.error('Ошибка при удалении раздела:', err);
        setError('Не удалось удалить раздел');
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewItems = (section) => {
    setSelectedSection(section);
    setActiveTab(1);
  };

  // Callback для результатов поиска
  const handleSearchResults = (items) => {
    setFilteredSections(items);
  };

  // Callback для изменения пагинации поиска
  const handleSearchPaginationChange = (paginationInfo) => {
    setSearchPagination(paginationInfo);
  };

  // Callback для изменения активности поиска
  const handleSearchActiveChange = (active) => {
    setSearchActive(active);
    if (!active) {
      // Сбрасываем на основные данные при очистке поиска
      setFilteredSections(sections);
      setSearchPage(1);
      setSearchPagination({ page: 1, pages: 1, total: 0, perPage: 20 });
    }
  };

  const handlePageChange = (e, value) => {
    if (searchActive) {
      // При активном поиске меняем страницу поиска
      setSearchPage(value);
    } else {
      // Обычная пагинация
      setPage(value);
      fetchSections(value);
    }
  };

  return (
    <Layout>
      {activeTab === 0 ? (
        <Box sx={{
          p: 3,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        }}>
          <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: { sm: 'space-between' }, gap: { xs: 2, sm: 0 } }}>
            <Typography variant="h4" component="h1" sx={{ mr: { sm: 'auto' } }}>
              Кастомные разделы
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' }, gap: { xs: 1, sm: 2 } }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Добавить раздел
              </Button>
            </Box>
          </Box>

          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Сортировать по</InputLabel>
              <Select
                value={sortBy}
                label="Сортировать по"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name">Названию</MenuItem>
                <MenuItem value="created_at">Дате создания</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              sx={{
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                }
              }}
            >
              {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
            </IconButton>
          </Box>

          <AsyncTableSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onResults={handleSearchResults}
            onActiveChange={handleSearchActiveChange}
            onPaginationChange={handleSearchPaginationChange}
            placeholder="Поиск разделов..."
            searchDoc="sections"
            searchFields={["name"]}
            page={searchPage}
            perPage={perPage}
            sx={{ mb: 3 }}
          />

          {error && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
              {error}
            </Paper>
          )}

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Список разделов" />
            {selectedSection && <Tab label={`Элементы раздела "${selectedSection.name}"`} />}
            {selectedSection && <Tab label="Скрипты" />}
          </Tabs>
          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'white' }}>Название</TableCell>
                  <TableCell sx={{ color: 'white' }}>Количество полей</TableCell>
                  <TableCell sx={{ color: 'white', position: 'sticky', right: 0, background: 'rgba(30,30,30,0.8)', zIndex: 2 }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell sx={{ color: 'white' }} colSpan={3} align="center">Загрузка...</TableCell>
                  </TableRow>
                ) : filteredSections.length === 0 ? (
                  <TableRow>
                    <TableCell sx={{ color: 'white' }} colSpan={3} align="center">
                      {searchQuery ? 'Разделы не найдены' : 'Разделы не найдены'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSections.map((section) => (
                    <TableRow key={section.id}>
                      <TableCell sx={{ color: 'white' }}>{section.name}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{section.schema ? section.schema.length : 0}</TableCell>
                      <TableCell sx={{ position: 'sticky', right: 0, background: 'rgba(30,30,30,0.7)', zIndex: 1 }}>
                        <IconButton sx={{ color: 'white' }} onClick={() => handleViewItems(section)}>
                          <List />
                        </IconButton>
                        <IconButton sx={{ color: 'white' }} onClick={() => handleOpenDialog(section)}>
                          <Edit />
                        </IconButton>
                        <IconButton sx={{ color: 'white' }} onClick={() => handleDelete(section.id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
          {/* Пагинация: разные данные для поиска и обычного режима */}
          {(() => {
            const currentPage = searchActive ? searchPagination.page : page;
            const currentTotalPages = searchActive ? searchPagination.pages : totalPages;
            const currentTotal = searchActive ? searchPagination.total : null;

            if (currentTotalPages <= 1 && !searchActive) return null;

            return (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
                {searchActive && currentTotal !== null && (
                  <Typography variant="body2" color="text.secondary">
                    Найдено: {currentTotal}
                  </Typography>
                )}
                {currentTotalPages > 1 && (
                  <Pagination
                    color="primary"
                    count={currentTotalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                  />
                )}
              </Box>
            );
          })()}
        </Box>
      ) : activeTab === 1 ? (
        selectedSection && (
          <>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
              <Tab label="Список разделов" />
              <Tab label={`Элементы раздела "${selectedSection.name}"`} />
              <Tab label="Скрипты" />
            </Tabs>
            <SectionItems section={selectedSection} />
          </>
        )
      ) : activeTab === 2 ? (
        selectedSection && (
          <>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
              <Tab label="Список разделов" />
              <Tab label={`Элементы раздела "${selectedSection.name}"`} />
              <Tab label="Скрипты" />
            </Tabs>
            <SectionScripts sectionId={selectedSection.id} />
          </>
        )
      ) : null}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingSection ? 'Редактировать раздел' : 'Добавить раздел'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Название"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
            />
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Схема раздела
            </Typography>
            <SchemaEditor
              schema={formData.schema}
              onChange={(newSchema) => setFormData(prev => ({ ...prev, schema: newSchema }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Layout>
  );
};

export default SectionsPage; 